import { Button, Group, Loader, Menu, Table, Text } from '@mantine/core';
import { IconDots, IconDownload, IconPlus } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { useFlags } from 'src/feature-flags/FlagsHook';
import dataCaptureClasses from 'src/data-capture/DataCapture.module.css';
import { useDeleteSuitabilityReport } from 'src/suitability-report/hooks/useDeleteSuitabilityReport';
import { useSuitabilityReports } from 'src/suitability-report/hooks/useSuitabilityReports';
import { useSuitability } from 'src/suitability-report/SuitabilityProvider';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import classes from './files-table/FilesTable.module.css';
import SuitabilityReportStatusPill from './SuitabilityReportStatusPill';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import FileUploadLayout from './FileUploadLayout';
import EmptyView from './components/EmptyView';
import FileNameWithIcon from './files-table/FileNameWithIcon';
import { dateMatchesSearch, formatDateTime } from 'src/meetings/utils/date-utils';
import { prefetchSuggestedActions } from 'src/UI/app-container/sidebar/prefetchSuggestedActions';

export default function SuitabilityReports() {
  const { suitabilityReports, isLoading, isError, error, refetch } = useSuitabilityReports();
  const { clientId } = useCurrentClient();
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();
  const {
    deleteSuitabilityReport,
    error: deleteSuitabilityReportError,
    reset: resetDeleteSuitabilityReport,
    isLoading: isDeletingSuitabilityReport,
  } = useDeleteSuitabilityReport();
  const { open: providerOpen } = useSuitability();
  const { flagEnabledDocumentEdit, flagEnabledNewLayout } = useFlags();

  const openSuitabilityFlow = flagEnabledNewLayout
    ? () =>
        navigate(`/client/${clientId}/suitability-report`, {
          state: { fromPath: location.pathname },
        })
    : providerOpen;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const seenSuccessIds = useRef<Set<string> | null>(null);
  const prevClientId = useRef(clientId);

  useEffect(() => {
    if (prevClientId.current !== clientId) {
      seenSuccessIds.current = null;
      prevClientId.current = clientId;
    }

    if (isLoading || !flagEnabledDocumentEdit || !clientId) return;

    const successIds = suitabilityReports
      .filter(r => (r.backgroundProcess?.status ?? 'SUCCESS') === 'SUCCESS')
      .map(r => r.id);

    if (seenSuccessIds.current === null) {
      seenSuccessIds.current = new Set(successIds);
      return;
    }

    for (const id of successIds) {
      if (seenSuccessIds.current.has(id)) continue;
      seenSuccessIds.current.add(id);
      prefetchSuggestedActions(queryClient, id, clientId);
    }
  }, [suitabilityReports, isLoading, flagEnabledDocumentEdit, clientId, queryClient]);

  const [searchValue, setSearchValue] = useState('');
  const [reportToDelete, setReportToDelete] = useState<{ id: string; displayName: string } | null>(
    null,
  );

  const handleDeletePress = (report: { id: string; displayName: string }) => {
    resetDeleteSuitabilityReport();
    setReportToDelete(report);
  };

  const handleDownload = async (reportFileId: number) => {
    try {
      const link = await createLink(reportFileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download suitability report',
        'An error occurred while trying to download the suitability report. Please try again later.',
        e,
        { clientId, reportFileId },
      );
      return;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;
    try {
      await deleteSuitabilityReport(clientId, reportToDelete.id);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete suitability report',
        'An error occurred while trying to delete the suitability report. Please try again later.',
        e,
        { clientId, reportId: reportToDelete.id },
      );
    }
    setReportToDelete(null);
    await refetch();
  };

  const filteredSuitabilityReports = searchValue.trim()
    ? suitabilityReports.filter(report => {
        const searchLower = searchValue.toLowerCase();
        return (
          (report.displayName || '').toLowerCase().includes(searchLower) ||
          dateMatchesSearch(report.createdAt, searchLower)
        );
      })
    : suitabilityReports;

  const rows = filteredSuitabilityReports.map(report => (
    <Table.Tr key={report.id}>
      <Table.Td className={classes.fileNameCell}>
        <FileNameWithIcon
          fileName={report.displayName}
          isFolder={false}
          isGenerated={report.isGenerated}
        />
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatDateTime(report.createdAt)}</Text>
      </Table.Td>
      <Table.Td>
        <SuitabilityReportStatusPill status={report.backgroundProcess?.status ?? 'SUCCESS'} />
      </Table.Td>
      <Table.Td align="right">
        <Group justify="flex-end" wrap="nowrap">
          {flagEnabledDocumentEdit &&
            (report.backgroundProcess?.status ?? 'SUCCESS') === 'SUCCESS' && (
              <Button
                variant="primary"
                onClick={() =>
                  navigate(`/client/${clientId}/document/suitability-reports/${report.id}/edit`)
                }
              >
                Edit
              </Button>
            )}
          {report.reportFileId && (
            <Button
              variant={flagEnabledDocumentEdit ? 'secondary' : 'primary'}
              onClick={() => handleDownload(report.reportFileId!)}
              rightSection={<IconDownload size={16} />}
            >
              Download
            </Button>
          )}
          <Menu
            transitionProps={{ transition: 'pop-top-right' }}
            position="bottom-end"
            width={220}
            withinPortal
          >
            <Menu.Target>
              <IconDots color="#475467" stroke={2} size={20} />
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => handleDeletePress(report)}>Delete</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  if (isError || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (filteredSuitabilityReports.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title="Suitability reports">
        <EmptyView
          title="Generate comprehensive suitability reports"
          subtitle="Create detailed suitability reports using Siesta AI to ensure compliance and provide clear investment recommendations."
          ctaText="Generate new"
          ctaIcon={IconPlus}
          onCtaClick={openSuitabilityFlow}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title="Suitability reports"
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      actions={
        <Button
          variant="primary"
          leftSection={<IconPlus size={16} />}
          size="md"
          onClick={openSuitabilityFlow}
        >
          Generate
        </Button>
      }
    >
      <Table.ScrollContainer
        minWidth={600}
        type="native"
        className={dataCaptureClasses.tableScrollContainer}
      >
        <Table
          variant="bare"
          captionSide="bottom"
          classNames={{ th: dataCaptureClasses.th }}
          verticalSpacing={'sm'}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Report Type</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                  <Loader size="sm" />
                </Table.Td>
              </Table.Tr>
            ) : (
              rows
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <ConfirmDeleteModal
        opened={!!reportToDelete}
        onClose={() => setReportToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete suitability report"
        message={`Are you sure you want to delete "${reportToDelete?.displayName}"? This action cannot be undone.`}
        error={deleteSuitabilityReportError}
        isLoading={isDeletingSuitabilityReport}
      />
    </FileUploadLayout>
  );
}
